import { Link } from 'react-router-dom'
import { TEXTOS } from '../../lib/glossario'
import { valorPorExtenso } from '../chamados/fiscalizacao/indicadores'
import { competenciaPorExtenso } from '../chamados/competencia'
import type { IndicadorDaSerie, LinhaDaSerie } from '../../types/indicadores'
import { ORDEM_DOS_INDICADORES, indicadorDe } from './serie'

/**
 * A série mês a mês, um indicador por coluna.
 *
 * **Célula sem número não vira zero, e é o ponto todo desta tabela.** Zero é o resultado de quem
 * cumpriu a meta; uma coluna de zeros silenciosos faria o contrato parecer bem executado justamente
 * nos meses em que ninguém mediu. Onde não há apuração, a célula mostra um traço com o motivo no
 * `title` — e o rodapé conta quantos ficaram de fora.
 *
 * @param props a série
 * @returns a tabela histórica
 */
export function TabelaDaSerie({ linhas }: { linhas: LinhaDaSerie[] }) {
  return (
    <div className="overflow-x-auto rounded-lg ring-1 ring-slate-200">
      <table className="min-w-full text-sm">
        <caption className="sr-only">{TEXTOS.indicadores.legenda}</caption>
        <thead className="bg-slate-50 text-left text-xs font-semibold text-slate-600">
          <tr>
            <th scope="col" className="px-3 py-2 font-semibold">
              Competência
            </th>
            {ORDEM_DOS_INDICADORES.map((codigo) => (
              <th key={codigo} scope="col" className="px-3 py-2 font-semibold">
                {codigo}
              </th>
            ))}
            <th scope="col" className="px-3 py-2 font-semibold">
              Desconto
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {linhas.map((linha) => (
            <tr key={linha.competencia} className="align-top">
              <th scope="row" className="whitespace-nowrap px-3 py-2 text-left font-medium text-slate-900">
                <Link to={`/fiscalizacao?competencia=${linha.competencia}`} className="hover:underline">
                  {competenciaPorExtenso(linha.competencia)}
                </Link>
                <SeloDaLinha linha={linha} />
              </th>
              {ORDEM_DOS_INDICADORES.map((codigo) => (
                <Celula key={codigo} indicador={indicadorDe(linha, codigo)} />
              ))}
              <Desconto linha={linha} />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Prévia ou congelado, escrito na própria linha — são números de naturezas diferentes. */
function SeloDaLinha({ linha }: { linha: LinhaDaSerie }) {
  const t = TEXTOS.indicadores
  if (linha.congeladoIlegivel) {
    return <span className="mt-0.5 block text-xs font-normal text-red-800">{t.congeladoIlegivel}</span>
  }
  return (
    <span className="mt-0.5 block text-xs font-normal text-slate-500">
      {linha.congelada ? t.congelada(linha.versao) : t.previa}
    </span>
  )
}

/** Um indicador num mês. Sem número, um traço com o motivo — nunca zero. */
function Celula({ indicador }: { indicador: IndicadorDaSerie | null }) {
  if (!indicador) {
    return <td className="px-3 py-2 text-slate-400">—</td>
  }
  if (!indicador.apurado) {
    return (
      <td className="cursor-help px-3 py-2 text-slate-400" title={indicador.motivoDaNaoApuracao ?? undefined}>
        <span aria-hidden="true">—</span>
        {/* O motivo também em texto, e não só no `title`: balão de mouse não existe no celular e
            não chega ao leitor de tela. Numa célula de tabela ele não cabe visível — cabe aqui. */}
        <span className="sr-only">{indicador.motivoDaNaoApuracao}</span>
      </td>
    )
  }
  return (
    <td className="whitespace-nowrap px-3 py-2 tabular-nums text-slate-900">
      {valorPorExtenso(indicador.codigo, indicador.valor) ?? TEXTOS.indicadores.somaDeDescontos}
      {indicador.redutorPercentual != null && indicador.redutorPercentual > 0 && (
        <span className="ml-1 text-xs font-semibold text-red-800">−{indicador.redutorPercentual}%</span>
      )}
    </td>
  )
}

/** O que o mês custou na nota: a soma bruta, o teto e a dispensa quando houve. */
function Desconto({ linha }: { linha: LinhaDaSerie }) {
  const t = TEXTOS.indicadores
  if (linha.redutorApurado == null) {
    return <td className="px-3 py-2 text-slate-400">—</td>
  }
  return (
    <td className="whitespace-nowrap px-3 py-2">
      <span className="font-semibold tabular-nums text-slate-900">−{linha.redutorApurado}%</span>
      {linha.somaDosRedutores != null && linha.somaDosRedutores > linha.redutorApurado && (
        <span className="ml-1 text-xs text-amber-800">{t.tetoCortou(linha.somaDosRedutores)}</span>
      )}
      {linha.sancoesDispensadas && <span className="mt-0.5 block text-xs text-slate-600">{t.dispensada}</span>}
    </td>
  )
}
